/**
 * Removes large files from an array
 * @param {array<Web File API Objects>} fileArray Array of web File API objects
 * @param {number} maxFileSaveMB Max allowed filesize in MB. Example 5 = 5MegaBytes
 * @param {function} [tooLargeFunction] Called with every file that is too large
 * @returns Array<File> that have a file size is less than or equal to the max file size
 */
export const removeLargeFilesFromArray = (
  fileArray,
  maxFileSaveMB,
  tooLargeFunction
) => {
  const MAX_FILE_SIZE_BYTES = maxFileSaveMB * 1000000
  const safeFiles = []
  let tooLarge
  if (tooLargeFunction) {
    tooLarge = tooLargeFunction
  } else {
    tooLarge = file => {
      const messageString = `${
        file.name
      } is too large. Image files can not be larger than ${maxFileSaveMB} MegaBytes.`
      // eslint-disable-next-line
      alert(messageString)
    }
  }

  fileArray.forEach(file => {
    const isTooLarge = file.size > MAX_FILE_SIZE_BYTES
    if (isTooLarge) {
      tooLarge(file)
    } else {
      safeFiles.push(file)
    }
  })

  return safeFiles
}

export const encodeImageFileAsURL = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

export const getFileExtensionFromType = imageType => {
  if (!imageType || !imageType.length) return false
  const safeImageType = imageType.trim()
  if (!safeImageType || !safeImageType.length) return false
  const indexOfSlash = safeImageType.indexOf('/')
  if (indexOfSlash === -1) return false
  const length = safeImageType.length - 1
  const extensionLength = length - indexOfSlash
  if (extensionLength <= 0) return false
  return safeImageType.slice(-1 * extensionLength)
}

/**
 * Builds a data object with an image encoded as base64
 * @param {File API Object} file A web File API object
 * @returns Object with { lastModified: Date object, name: "file.png", size: 409738, type: "image/png", base64: "data:as89e3hu" }
 */
export const processFile = async file => {
  const base64 = await encodeImageFileAsURL(file)
  const resultObject = {}
  resultObject.extension = getFileExtensionFromType(file.type) // string
  resultObject.lastModified = new Date(file.lastModified) // convert time to JS Date
  resultObject.name = file.name // string
  resultObject.size = file.size // In bytes
  resultObject.type = file.type // string
  resultObject.base64 = base64 // string
  return resultObject
}

export const stripBase64Start = base64String => {
  const firstComma = base64String.indexOf(',') + 1
  return base64String.slice(firstComma)
}
